import { useCallback, useRef } from 'react';

import customNanoId from 'lib/nanoid/nanoid';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import { NegativeButton } from 'approot/shared/styles/buttons';
import { PStyled } from 'approot/shared/styles/typography.style';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { Dropdown, DropdownIndicator } from 'approot/shared/dropdown/dropdown';
import { DELETE_ACTIVITIES_COLUMNS } from '../columns.graphql';
import type {
  DeleteActivitiesColumnsMutation,
  DeleteActivitiesColumnsMutationVariables,
} from '../columns.graphql.generated';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import {
  useGetInteractiveLessonParams,
  useUpdateComponent,
} from 'approot/activity-builder/activity-builder.hooks';
import { SETTINGS_MENU_ID } from 'approot/activity-builder/settings/settings-component-menu';
import { SettingProps } from './column-settings.constants';
import { ColumnDropdownButton } from './column-settings.style';
import { ComponentColumn } from '../../component.constants';
import { getColumnsColumnPayload } from './column-settings.utils';
import {
  ACTIVITIES_CONTENT_STATUS_DRAFT,
  LANGUAGE_CODE_EN_AU,
} from 'global/constants';

const ColumnSettingsNumberOfColumns = ({
  id,
  sort,
  type,
  columnProperties,
  columns,
  sectionId,
  contentId,
}: SettingProps & {
  contentId: number;
}) => {
  const numberRef = useRef<number>(0);
  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const { contentType } = useGetInteractiveLessonParams();

  const [deleteColumns] = useLoggingMutation<
    DeleteActivitiesColumnsMutation,
    DeleteActivitiesColumnsMutationVariables
  >(DELETE_ACTIVITIES_COLUMNS);

  const { updateComponent } = useUpdateComponent();

  const doRemovedColumnsHaveComponents = useCallback(
    (value: number) => {
      if (!columns) return false;
      for (let i = value; i < columns.length; i++) {
        if (columns[i].components.length > 0) {
          return true;
        }
      }
      return false;
    },
    [columns]
  );

  const handleColumnCountUpdate = useCallback(
    (numberOfColumns: number) => {
      if (!columns) return;

      if (numberOfColumns > columns.length) {
        const newColSpan = 12 / numberOfColumns;
        const updatedColumns: ComponentColumn[] = [];

        for (let i = 0; i < numberOfColumns; i++) {
          if (columns[i]) {
            updatedColumns.push({
              ...columns[i],
              colspan: newColSpan,
            });
          } else {
            updatedColumns.push({
              alignment: columns[0].alignment,
              colspan: newColSpan,
              columnId: customNanoId(),
              components: [],
              index: i,
              style: null,
              language: LANGUAGE_CODE_EN_AU,
              version: ACTIVITIES_CONTENT_STATUS_DRAFT,
              __typename: 'ActivitiesColumnComponent',
            });
          }
        }
        updateComponent(
          {
            componentId: id,
            sectionId,
            componentData: {
              type,
              sort,
              properties: {
                columns: getColumnsColumnPayload(updatedColumns),
              },
            },
          },
          {
            updateActivitiesComponent: {
              id,
              sectionId,
              language: LANGUAGE_CODE_EN_AU,
              version: ACTIVITIES_CONTENT_STATUS_DRAFT,
              sort,
              type,
              columnProperties,
              columns: updatedColumns,
              __typename: 'ActivitiesColumnsComponent',
            },
          }
        );
      } else if (numberOfColumns < columns.length) {
        const removed = columns
          .slice()
          .splice(numberOfColumns, columns.length - numberOfColumns);
        const newColSpan = 12 / (columns.length - removed.length);
        const removedIds = removed.map(c => c.columnId);
        deleteColumns({
          variables: {
            input: {
              contentId,
              contentType,
              sectionId,
              componentId: id,
              columnsId: removedIds,
              colspan: newColSpan,
            },
          },
          optimisticResponse: {
            deleteActivitiesColumns: {
              id,
              sectionId,
              language: LANGUAGE_CODE_EN_AU,
              version: ACTIVITIES_CONTENT_STATUS_DRAFT,
              sort,
              type,
              columnProperties,
              columns: columns
                .filter(c => !removedIds.includes(c.columnId))
                .map(c => ({
                  ...c,
                  colspan: newColSpan,
                })),
              __typename: 'ActivitiesColumnsComponent',
            },
          },
        });
      }
    },
    [
      id,
      sectionId,
      contentId,
      contentType,
      sort,
      type,
      columns,
      columnProperties,
      deleteColumns,
      updateComponent,
    ]
  );

  return (
    <>
      {isVisible && (
        <ActivityBuilderModal
          isVisible
          title="Change the Number of Columns"
          headerTitle="Change the Number of Columns"
          actions={
            <NegativeButton
              type="button"
              onClick={() => {
                closeModal();
                handleColumnCountUpdate(numberRef.current);
              }}
            >
              Delete Columns
            </NegativeButton>
          }
          onClose={closeModal}
        >
          <PStyled>
            Are you sure you want to change the number of columns?
          </PStyled>
          <PStyled>
            <strong>
              Removing columns will also delete the content you’ve added in the
              ones to the right.
            </strong>
          </PStyled>
        </ActivityBuilderModal>
      )}
      <Dropdown
        isSmall
        menuWidth={202}
        menuContainerElement={SETTINGS_MENU_ID}
        strategy="fixed"
        closeMenuOnSelect={false}
        onMenuItemSelect={options => {
          setTimeout(() => options.closeDropdown());
        }}
        dropdownButtonContents={
          <ColumnDropdownButton data-selector="column-no-of-columns">
            {columns?.length} columns
            <DropdownIndicator />
          </ColumnDropdownButton>
        }
        dropdownContents={
          <SettingsGroup marginBottom={0}>
            <SettingsLabel>Number of Columns</SettingsLabel>
            <CheckableSingle
              name="numberOfColumns"
              data-dropdown-item="true"
              options={[
                { label: '2', value: 2 },
                { label: '3', value: 3 },
                { label: '4', value: 4 },
                { label: '5', value: 5 },
                { label: '6', value: 6 },
                { label: '7', value: 7 },
              ]}
              selected={columns?.length || 2}
              onSelect={value => {
                if (doRemovedColumnsHaveComponents(value)) {
                  numberRef.current = value;
                  openModal();
                } else {
                  handleColumnCountUpdate(value);
                }
              }}
            />
          </SettingsGroup>
        }
      />
    </>
  );
};

export default ColumnSettingsNumberOfColumns;
